import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    Paper,
} from '@mui/material';
import { Search, Mail, CheckCircle, FilterList, Clear } from '@mui/icons-material';
import { VideoCard } from './VideoCard';
import { VideoManagementPagination } from './VideoManagementPagination';
import { FilterModal } from './FilterModal';
import { IVideoSubmission, IVideoManagementFilters } from './types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import _ from 'lodash';

export interface IVideoManagementPageProps {
    submissions: IVideoSubmission[];
    pagination: any;
    loading: boolean;
    error: string | null;
    selectedVideos: string[];
    onVideoSelect: (videoId: string, checked: boolean) => void;
    onSelectAll: () => void;
    onSendEmail: () => void;
    onFiltersChange: (filters: Partial<IVideoManagementFilters>) => void;
    onPreselect?: (submissionIds: string[], selection_state: 'selected' | 'not_selected') => void;
    filters: IVideoManagementFilters;
}

export const VideoManagementPage: React.FC<IVideoManagementPageProps> = ({
    submissions,
    pagination,
    loading,
    error,
    selectedVideos,
    onVideoSelect,
    onSelectAll,
    onSendEmail,
    onFiltersChange,
    onPreselect,
    filters,
}) => {
    const [searchQuery, setSearchQuery] = useState(filters?.searchTerm || '');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const { themeStyle } = useThemeContext();

    // Remove client-side filtering - all filtering is now done through API
    // The submissions array from the API already contains the filtered results
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            if (onFiltersChange) {
                onFiltersChange({ searchTerm: searchText, page: 1, limit: 9 });
            }
        }, 500),
    ).current;

    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    // Handle search input change with debouncing
    const handleSearchChange = (event: any) => {
        const value = event.target.value;
        setSearchQuery(value);

        // Only trigger API search if query is longer than 2 characters or empty
        if (value.length === 0 || value.length > 4) {
            debouncedSearchHandler(value);
        }
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchQuery('');
        onFiltersChange({ searchTerm: '', page: 1 });
    };

    // Handle select all
    const handleSelectAll = () => {
        onSelectAll();
    };

    // Handle send email
    const handleSendEmail = () => {
        onSendEmail();
    };

    // Handle filter modal open/close
    const handleFilterModalOpen = () => setIsFilterModalOpen(true);
    const handleFilterModalClose = () => setIsFilterModalOpen(false);

    // Handle filter changes
    const handleFilterChange = (newFilters: Partial<IVideoManagementFilters>) => {
        onFiltersChange({ ...newFilters, page: 1 });
    };

    // Check if there are active filters
    const hasActiveFilters =
        (filters && filters.category && filters?.category.length > 0) ||
        (filters && filters?.selection_state !== '') ||
        (filters && filters?.selection_email_sent !== '') ||
        (filters && filters?.pre_selection_completed !== '');

    // Get active filter count
    const activeFilterCount = [
        filters?.category?.length || 0,
        filters?.selection_state ? 1 : 0,
        filters?.selection_email_sent !== '' ? 1 : 0,
        filters?.pre_selection_completed !== '' ? 1 : 0,
    ].reduce((sum, count) => sum + count, 0);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme[themeStyle].deep_grey }}>
            {/* Header */}
            <Paper
                elevation={1}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: theme[themeStyle].deep_grey,
                }}>
                <Container maxWidth={false} sx={{ py: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            justifyContent: 'space-between',
                            gap: 3,
                        }}>
                        <Box>
                            <Typography
                                variant="h4"
                                component="h1"
                                fontWeight="bold"
                                gutterBottom
                                sx={{ color: theme[themeStyle].tag_white }}>
                                Submissions Management
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ color: theme[themeStyle].tag_white }}>
                                Search, select, and manage campaign submissions.
                            </Typography>
                        </Box>

                        {/* Search and Filter Controls */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                width: { xs: '100%', md: 'auto' },
                            }}>
                            {/* Search Bar */}
                            <Box
                                sx={{
                                    maxWidth: 400,
                                    width: '100%',
                                    order: { xs: 2, sm: 1 },
                                }}>
                                <TextField
                                    autoFocus
                                    variant="outlined"
                                    placeholder="Search videos by creator name..."
                                    color="secondary"
                                    value={searchQuery}
                                    InputProps={{
                                        placeholder: 'Search videos by creator name...',
                                        style: {
                                            color: theme[themeStyle].lightWhite,
                                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                                            fontSize: 14,
                                            fontWeight: '300',
                                            borderRadius: '50px',
                                            paddingLeft: '8px',
                                            backgroundColor: theme[themeStyle].gray_text,
                                            minWidth: 300,
                                            height: 42,
                                            cursor: 'pointer',
                                        },
                                        startAdornment: (
                                            <InputAdornment sx={{ paddingLeft: 1 }} position="start">
                                                <SearchIcon sx={{ color: theme[themeStyle].darkGray }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <>
                                                {searchQuery.length > 0 && (
                                                    <InputAdornment position="end">
                                                        <Button
                                                            size="small"
                                                            onClick={handleClearSearch}
                                                            sx={{ minWidth: 'auto', p: 0.5 }}>
                                                            <CloseIcon />
                                                        </Button>
                                                    </InputAdornment>
                                                )}
                                            </>
                                        ),
                                    }}
                                    onChange={handleSearchChange}
                                    sx={{
                                        pb: 1,
                                        width: '100%',
                                        cursor: 'pointer',
                                    }}
                                />
                            </Box>

                            {/* Filter Button */}
                            <Box sx={{ order: { xs: 1, sm: 2 } }}>
                                <Button
                                    variant={hasActiveFilters ? 'contained' : 'outlined'}
                                    onClick={handleFilterModalOpen}
                                    startIcon={<FilterList />}
                                    sx={{
                                        minWidth: { xs: '100%', sm: 120 },
                                        height: 42,
                                        borderRadius: '50px',
                                        backgroundColor: hasActiveFilters ? 'primary.main' : 'transparent',
                                        color: hasActiveFilters ? 'white' : theme[themeStyle].tag_white,
                                        borderColor: hasActiveFilters ? 'primary.main' : theme[themeStyle].tag_white,
                                        '&:hover': {
                                            backgroundColor: hasActiveFilters
                                                ? 'primary.dark'
                                                : 'rgba(255,255,255,0.1)',
                                        },
                                    }}>
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <Chip
                                            label={activeFilterCount}
                                            size="small"
                                            sx={{
                                                ml: 1,
                                                backgroundColor: 'white',
                                                color: 'primary.main',
                                                fontSize: '0.75rem',
                                                height: 20,
                                            }}
                                        />
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Paper>

            {/* Main Content */}
            <Container
                maxWidth={false}
                sx={{
                    py: 4,
                    height: 'calc(100vh - 200px)', // Account for header height
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        },
                    },
                    // Smooth scrolling for better UX
                    scrollBehavior: 'smooth',
                    // Ensure content doesn't get cut off
                    pb: 8,
                }}>
                {/* Controls Bar */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 4,
                        position: 'sticky',
                        top: 0,
                        backgroundColor: theme[themeStyle].deep_grey,
                        zIndex: 10,
                        py: 2,
                        borderRadius: 1,
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}>
                        <Button
                            variant="outlined"
                            onClick={handleSelectAll}
                            startIcon={<CheckCircle />}
                            sx={{ minWidth: 140 }}>
                            {selectedVideos?.length === submissions?.length ? 'Deselect All' : 'Select All'}
                        </Button>

                        {selectedVideos?.length > 0 && (
                            <Chip
                                label={`${selectedVideos?.length} selected`}
                                color="primary"
                                variant="filled"
                                sx={{ px: 2, py: 1 }}
                            />
                        )}

                        {/* Active Filters Display */}
                        {hasActiveFilters && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Active filters:
                                </Typography>
                                <Chip
                                    label={`${activeFilterCount} active`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                />
                                <Button
                                    size="small"
                                    onClick={() =>
                                        onFiltersChange({
                                            category: [],
                                            selection_state: '',
                                            selection_email_sent: '',
                                            pre_selection_completed: '',
                                        })
                                    }
                                    startIcon={<Clear />}
                                    sx={{ minWidth: 'auto' }}>
                                    Clear
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* Send Email Button */}
                    {selectedVideos?.length > 0 && (
                        <Button
                            variant="contained"
                            onClick={handleSendEmail}
                            startIcon={<Mail />}
                            sx={{
                                minWidth: 160,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 100,
                                height: '40px',

                                textTransform: 'none',
                            }}>
                            Send Email ({selectedVideos.length})
                        </Button>
                    )}
                </Box>

                {/* Results Info */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white }}>
                        Showing {submissions?.length || 0} of {pagination?.totalCount || 0} videos
                        {searchQuery && <span style={{ marginLeft: '4px' }}>for "{searchQuery}"</span>}
                        {hasActiveFilters && (
                            <span style={{ marginLeft: '8px', color: 'text.secondary' }}>(filtered)</span>
                        )}
                        {filters && filters.sort && (
                            <span style={{ marginLeft: '8px', color: 'text.secondary' }}>
                                • sorted by{' '}
                                {filters.sort === 'total_average'
                                    ? 'score'
                                    : filters.sort === 'total_votes'
                                    ? 'votes'
                                    : 'date'}
                                ({filters.order === 'desc' ? 'highest first' : 'lowest first'})
                            </span>
                        )}
                    </Typography>
                </Box>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 8,
                        }}>
                        <CircularProgress size={48} />
                    </Box>
                )}

                {/* Video Grid */}
                {!loading && (!submissions || submissions.length === 0) ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 12,
                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            minHeight: '400px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <Search sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: theme[themeStyle].tag_white }} gutterBottom>
                            No submissions found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white }}>
                            {searchQuery || hasActiveFilters
                                ? 'Try adjusting your search terms or filters'
                                : 'No submissions available for this contest'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Grid container spacing={3}>
                                {submissions?.map(video => (
                                    <Grid item xs={12} sm={6} lg={4} key={video._id}>
                                        <VideoCard
                                            video={video}
                                            selected={selectedVideos?.includes(video._id)}
                                            onSelect={onVideoSelect}
                                            onPreselect={onPreselect}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        {/* Pagination */}
                        {pagination && (
                            <Box sx={{ mt: 4, mb: 2 }}>
                                <VideoManagementPagination
                                    pagination={pagination}
                                    onPageChange={page => onFiltersChange({ page })}
                                    loading={loading}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Container>

            {/* Filter Modal */}
            <FilterModal
                open={isFilterModalOpen}
                onClose={handleFilterModalClose}
                filters={filters}
                onApplyFilters={handleFilterChange}
            />
        </Box>
    );
};
